import { VerifiedUser } from '@mui/icons-material'
import {
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography
} from '@mui/material'
import { ExplanationAccordion, SortableTableCell } from 'components'
import { useLayout } from 'context'
import * as controllers from 'controllers'
import { DocumentDto } from 'dtos'
import { useLoadingState } from 'hooks'
import { DEBOUNCE_DELAY } from 'localConstants'
import moment from 'moment'
import { DocumentsParameters } from 'parameters'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDebounce } from 'utils'
import OCRUploadFilesCard from './OCRUploadFilesCard'

export default function OCR() {
  const layout = useLayout()
  const navigate = useNavigate()

  const [documents, setDocuments] = useState<DocumentDto[]>([])
  const [loadingState, setLoadingState] = useLoadingState({ isGettingOCRDocuments: false })
  const [parameters, setParameters] = useState<DocumentsParameters>({
    page: 0,
    pageSize: 10,
    orderBy: 'fileName',
    order: 'asc'
  })
  const [totalCount, setTotalCount] = useState<number>(0)

  const onChangeSortByDirection = (orderBy: string) => {
    setParameters(prev => ({
      ...parameters,
      orderBy,
      order: prev.orderBy === orderBy && prev.order === 'asc' ? 'desc' : 'asc'
    }))
  }

  const getOCRUploadedFiles = useDebounce((parameters: DocumentsParameters) => {
    setLoadingState('isGettingOCRDocuments', true)
    controllers.ocr
      .getOCRUploadedFiles(parameters)
      .then(response => {
        setTotalCount(response.totalCount ?? 0)
        setDocuments(response.value)
      })
      .finally(() => {
        setLoadingState('isGettingOCRDocuments', false)
      })
  }, DEBOUNCE_DELAY)

  useEffect(() => {
    if (!getOCRUploadedFiles) {
      return
    }

    getOCRUploadedFiles(parameters)
  }, [parameters, getOCRUploadedFiles])

  useEffect(() => {
    layout.setIsLayoutLoading(loadingState.isGettingOCRDocuments)
  }, [layout, loadingState.isGettingOCRDocuments])

  return (
    <>
      <Grid container spacing={2} alignItems='center' justifyContent='space-between'>
        <Grid item xs={12}>
          <OCRUploadFilesCard
            refresh={() => {
              getOCRUploadedFiles(parameters)
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Grid container justifyContent='center'>
                <Grid item xs={12} container spacing={2} alignItems='center'>
                  <Grid item xs={12}>
                    <Typography fontWeight='bold' variant='h5'>
                      Uploaded Files
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Divider />
                  </Grid>

                  <Grid item xs={12}>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <SortableTableCell
                              onChangeSortByDirection={onChangeSortByDirection}
                              propertyName='fileName'
                              sortBy={parameters.orderBy}
                              sortDirection={parameters.order}
                            >
                              Uploaded File Name
                            </SortableTableCell>

                            <SortableTableCell
                              onChangeSortByDirection={onChangeSortByDirection}
                              propertyName='createdDateTime'
                              sortBy={parameters.orderBy}
                              sortDirection={parameters.order}
                            >
                              Date/Time Uploaded
                            </SortableTableCell>

                            <SortableTableCell
                              onChangeSortByDirection={onChangeSortByDirection}
                              propertyName='documentType'
                              sortBy={parameters.orderBy}
                              sortDirection={parameters.order}
                            >
                              Doc Type
                            </SortableTableCell>

                            <TableCell padding='checkbox'></TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {documents.map(document => (
                            <TableRow key={document.documentGuid}>
                              <TableCell>{document.displayFileName}</TableCell>

                              <TableCell>{moment(document.createdDateTime).format('M/D/yyyy h:mm A')}</TableCell>

                              <TableCell>{document.documentType?.name}</TableCell>

                              <TableCell padding='checkbox'>
                                <Button
                                  color='primary'
                                  endIcon={<VerifiedUser />}
                                  onClick={() => {
                                    navigate(`/ocr/${encodeURIComponent(document.documentGuid!)}`)
                                  }}
                                  variant='text'
                                >
                                  Verify
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>

                        <TableFooter sx={{ width: '100%' }}>
                          <TableRow>
                            <TablePagination
                              rowsPerPageOptions={[5, 10, 25]}
                              SelectProps={{ fullWidth: false }}
                              count={totalCount}
                              rowsPerPage={parameters.pageSize}
                              page={parameters.page}
                              onPageChange={(_, page) => {
                                setParameters({
                                  ...parameters,
                                  page
                                })
                              }}
                              onRowsPerPageChange={e => {
                                setParameters({
                                  ...parameters,
                                  page: 0,
                                  pageSize: e.target.value as unknown as number
                                })
                              }}
                            />
                          </TableRow>
                        </TableFooter>
                      </Table>
                    </TableContainer>
                  </Grid>

                  <Grid item xs={12}>
                    <ExplanationAccordion variant='outlined'>
                      || Files uploaded into the OCR feature that have not been verified will appear in the Uploaded Files list.
                      If the OCR process was able to identify the type of file that was uploaded and OCR'd, the "Doc Type"
                      Column will display the detected doc type, otherwise the Doc Type column will show "Unknown". || Any 7501
                      or Broker Invoice documents emailed to BWI and automatically picked up by the background process that
                      brings these two documents into VTS2 will appear in the list of Uploaded Files for users to verify. ||
                    </ExplanationAccordion>
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  )
}
